import { useState, useEffect } from "react";
import { LogIn, ShieldX, FileUp, FilePen, Trash2, UserPlus, UserMinus, UserCog, FileSpreadsheet, KeyRound, RotateCcw, Lock, Unlock, RefreshCw, AlertTriangle, X, Eraser } from "lucide-react";
import { useApp } from "../context/AppContext";
import { authAPI, reportAPI, imagingModalityAPI } from "../utils/api";
import { useTheme } from "../context/ThemeContext";
import { systemAPI } from "../utils/api";

const ALL_ROLES = [
  { id: "admin",        label: "Administration" },
  { id: "doctor",       label: "Doctor" },
  { id: "technician",   label: "Technician" },
  { id: "receptionist", label: "Receptionist" },
];

function ConfirmDialog({ open, title, message, confirmLabel, variant, onConfirm, onCancel }) {
  if (!open) return null;
  const isDanger  = variant === "danger";
  const isWarning = variant === "warning";
  const iconBg    = isDanger ? "#FEE2E2" : isWarning ? "#FEF3C7" : "#F1F5F9";
  const iconColor = isDanger ? "#DC2626" : isWarning ? "#D97706" : "#64748B";
  const btnBg     = isDanger ? "#DC2626" : isWarning ? "#D97706" : "#0F172A";
  const Icon      = isDanger ? Trash2 : isWarning ? Eraser : AlertTriangle;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ background: "var(--surface-2,#fff)", border: "0.5px solid var(--border,#E2E8F0)",
        borderRadius: 12, width: 380, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}>
        <div style={{ padding: "20px 20px 0" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: iconBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon size={18} color={iconColor} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 500, color: "var(--text-primary,#0F172A)", marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary,#64748B)", lineHeight: 1.5 }}>{message}</div>
            </div>
          </div>
        </div>
        <div style={{ padding: "16px 20px 20px", display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <button onClick={onCancel}
            style={{ padding: "7px 16px", borderRadius: 6, border: "0.5px solid #E2E8F0",
              background: "transparent", fontSize: 13, color: "var(--text-secondary,#64748B)", cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ padding: "7px 16px", borderRadius: 6, border: "none",
              background: btnBg, fontSize: 13, fontWeight: 500, color: "#fff", cursor: "pointer" }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Settings() {
  const { t } = useTheme();
  const { user, changePassword, verifyMasterCode, updateMasterCode, getAuditLog, mfaSetup, mfaVerifySetup, mfaDisable, mfaStatus, mfaRegenerateBackupCodes } = useApp();
  const isAdmin = user?.role === "admin";

  const [tab, setTab]       = useState("passwords");
  const [maintenanceOn, setMaintenanceOn] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [target, setTarget] = useState(isAdmin ? "doctor" : user?.role || "doctor");
  const [newPass, setNew]   = useState("");
  const [confirm, setCon]   = useState("");
  const [showPass, setShowPass] = useState(false);
  const [success, setSucc]  = useState("");
  const [error, setErr]     = useState("");

  // Emergency reset
  const [emgCode, setEmgCode] = useState("");
  const [emgNew, setEmgNew]   = useState("");
  const [emgCon, setEmgCon]   = useState("");
  const [emgErr, setEmgErr]   = useState("");
  const [emgSucc, setEmgSucc] = useState("");

  // Master code update (admin only) — deprecated, hidden once MFA is enabled
  const [mcCurrent, setMcCurrent] = useState("");
  const [mcNew, setMcNew]         = useState("");
  const [mcCon, setMcCon]         = useState("");
  const [mcErr, setMcErr]         = useState("");
  const [mcSucc, setMcSucc]       = useState("");

  // Two-factor authentication (MFA) — admin only
  const [mfaEnabled,      setMfaEnabled]      = useState(false);
  const [mfaStatusLoading,setMfaStatusLoading]= useState(true);
  const [mfaBackupRemaining, setMfaBackupRemaining] = useState(0);
  const [mfaSetupStep,    setMfaSetupStep]    = useState(null); // null | "qr" | "verify" | "backup-codes"
  const [mfaQrCode,       setMfaQrCode]       = useState(null);
  const [mfaSecretText,   setMfaSecretText]   = useState("");
  const [mfaSetupCode,    setMfaSetupCode]    = useState("");
  const [mfaSetupErr,     setMfaSetupErr]     = useState("");
  const [mfaSetupLoading, setMfaSetupLoading] = useState(false);
  const [mfaDisableCode,  setMfaDisableCode]  = useState("");
  const [mfaDisableErr,   setMfaDisableErr]   = useState("");
  const [mfaDisableLoading,setMfaDisableLoading] = useState(false);
  const [showDisableMfa,  setShowDisableMfa]  = useState(false);
  const [newBackupCodes,  setNewBackupCodes]  = useState([]); // shown once, after setup or regenerate
  const [showRegenBackup, setShowRegenBackup] = useState(false);
  const [regenCode,       setRegenCode]       = useState("");
  const [regenErr,        setRegenErr]        = useState("");
  const [regenLoading,    setRegenLoading]    = useState(false);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);

  // PIN lock
  const [pin1,     setPin1]     = useState("");
  const [pin2,     setPin2]     = useState("");
  const [pinErr,   setPinErr]   = useState("");
  const [pinSucc,  setPinSucc]  = useState("");
  const [hasPin,   setHasPin]   = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  // Admin recovery email
  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminEmailMsg, setAdminEmailMsg] = useState("");
  const [adminEmailErr, setAdminEmailErr] = useState("");
  const [emailLoading,  setEmailLoading]  = useState(false);

  // Audit log
  const [auditLog, setAuditLog]         = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [blockedIPs, setBlockedIPs]       = useState([]);
  const [ipLoading, setIpLoading]         = useState(false);
  const [blockMsg, setBlockMsg]           = useState("");
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRole,    setAuditRole]    = useState("all");
  const [auditAction,  setAuditAction]  = useState("all");
  const [auditSearch,  setAuditSearch]  = useState("");
  const [auditPeriod,  setAuditPeriod]  = useState("today");

  useEffect(() => {
    // Load admin recovery email if admin
    if (isAdmin) {
      import("../utils/api").then(({ authAPI }) => {
        authAPI.getAdminEmail().then(data => {
          if (data.email) setAdminEmail(data.email);
        }).catch(() => {});
      });
    }
    // Check if PIN is set for current user
    if (user?.role === "admin" || user?.role === "doctor") {
      import("../utils/api").then(({ authAPI }) => {
        authAPI.hasPin(user.role).then(data => setHasPin(data.has_pin)).catch(() => {});
      });
    }
  }, []);

  useEffect(() => {
    // Fetch current maintenance status
    fetch((window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/backup/maintenance")
      .then(r => r.json())
      .then(data => setMaintenanceOn(data.maintenance || false))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "audit" && isAdmin && loginAttempts.length === 0) {
      setIpLoading(true);
      Promise.all([
        authAPI.getLoginAttempts().catch(() => ({ attempts: [] })),
        authAPI.getBlockedIPs().catch(() => ({ blocked_ips: [] })),
      ]).then(([att, blk]) => {
        setLoginAttempts(att.attempts || []);
        setBlockedIPs(blk.blocked_ips || []);
        setIpLoading(false);
      }).catch(() => setIpLoading(false));
    }
    if (tab === "audit" && isAdmin && auditLog.length === 0) {
      setAuditLoading(true);
      getAuditLog(500, "today").then(data => { setAuditLog(Array.isArray(data) ? data : []); setAuditLoading(false); }).catch(() => setAuditLoading(false));
    }
  }, [tab]);

  // Load MFA status on mount (not just when the tab is opened) so the
  // Emergency Admin Reset card can be hidden correctly from the very first render.
  useEffect(() => {
    if (isAdmin) {
      setMfaStatusLoading(true);
      mfaStatus().then(res => {
        setMfaEnabled(res.success ? res.enabled : false);
        setMfaBackupRemaining(res.success ? res.backupCodesRemaining : 0);
        setMfaStatusLoading(false);
      });
    } else {
      setMfaStatusLoading(false);
    }
  }, []);

  const startMfaSetup = async () => {
    setMfaSetupErr(""); setMfaSetupLoading(true);
    const res = await mfaSetup();
    setMfaSetupLoading(false);
    if (!res.success) { setMfaSetupErr(res.error || "Could not start MFA setup."); return; }
    setMfaQrCode(res.qrCodePng);
    setMfaSecretText(res.secret);
    setMfaSetupStep("qr");
  };

  const confirmMfaSetup = async () => {
    setMfaSetupErr("");
    if (mfaSetupCode.length !== 6) return setMfaSetupErr("Enter the 6-digit code from your authenticator app.");
    setMfaSetupLoading(true);
    const res = await mfaVerifySetup(mfaSetupCode.trim());
    setMfaSetupLoading(false);
    if (!res.success) { setMfaSetupErr(res.error || "Invalid code. Try again."); return; }
    setMfaEnabled(true);
    setMfaBackupRemaining(res.backupCodes?.length || 0);
    setNewBackupCodes(res.backupCodes || []);
    setCodesAcknowledged(false);
    setMfaSetupStep("backup-codes");
    setMfaQrCode(null);
    setMfaSecretText("");
    setMfaSetupCode("");
  };

  const finishMfaSetup = () => {
    setMfaSetupStep(null);
    setNewBackupCodes([]);
    setCodesAcknowledged(false);
  };

  const cancelMfaSetup = () => {
    setMfaSetupStep(null); setMfaQrCode(null); setMfaSecretText(""); setMfaSetupCode(""); setMfaSetupErr("");
  };

  const handleRegenerateBackupCodes = async () => {
    setRegenErr("");
    if (regenCode.length !== 6) return setRegenErr("Enter your current 6-digit authenticator code.");
    setRegenLoading(true);
    const res = await mfaRegenerateBackupCodes(regenCode.trim());
    setRegenLoading(false);
    if (!res.success) { setRegenErr(res.error || "Could not regenerate backup codes."); return; }
    setMfaBackupRemaining(res.backupCodes?.length || 0);
    setNewBackupCodes(res.backupCodes || []);
    setCodesAcknowledged(false);
    setShowRegenBackup(false);
    setRegenCode("");
    setMfaSetupStep("backup-codes");
  };

  const handleMfaDisable = async () => {
    setMfaDisableErr("");
    if (mfaDisableCode.length !== 6) return setMfaDisableErr("Enter your current 6-digit authenticator code.");
    setMfaDisableLoading(true);
    const res = await mfaDisable(mfaDisableCode.trim());
    setMfaDisableLoading(false);
    if (!res.success) { setMfaDisableErr(res.error || "Could not disable MFA."); return; }
    setMfaEnabled(false);
    setShowDisableMfa(false);
    setMfaDisableCode("");
  };

  const handleChange = async () => {
    setErr(""); setSucc("");
    if (!newPass)           return setErr("Password cannot be empty.");
    if (newPass.length < 8) return setErr("Password must be at least 8 characters.");
    if (newPass !== confirm) return setErr("Passwords do not match.");
    // Non-admin can only change their own role's password
    if (!isAdmin && target !== user.role) return setErr("You can only change your own password.");
    try {
      await changePassword(target, newPass, user.role);
      setSucc(`${ALL_ROLES.find(r => r.id === target)?.label} password updated.`);
      setNew(""); setCon("");
    } catch (e) { setErr(e.message); }
  };

  const handleEmergency = async () => {
    setEmgErr(""); setEmgSucc("");
    if (!emgNew)            return setEmgErr("New password cannot be empty.");
    if (emgNew.length < 8)  return setEmgErr("Password must be at least 8 characters.");
    if (emgNew !== emgCon)  return setEmgErr("Passwords do not match.");
    const res = await verifyMasterCode(emgCode, emgNew);
    if (res.success) { setEmgSucc("Admin password reset via master code."); setEmgCode(""); setEmgNew(""); setEmgCon(""); }
    else setEmgErr(res.error || "Invalid master code.");
  };

  const handleMasterCodeUpdate = async () => {
    setMcErr(""); setMcSucc("");
    if (!mcNew)           return setMcErr("New code cannot be empty.");
    if (mcNew.length < 8) return setMcErr("Master code must be at least 8 characters.");
    if (mcNew !== mcCon)  return setMcErr("Codes do not match.");
    const res = await updateMasterCode(mcCurrent, mcNew);
    if (res.success) { setMcSucc("Master code updated successfully."); setMcCurrent(""); setMcNew(""); setMcCon(""); }
    else setMcErr(res.error || "Failed to update master code.");
  };

  const TABS = [
    { id: "passwords", label: t.passwordsTab },
    ...(isAdmin ? [{ id: "mfa",        label: t.twoFactorTab }] : []),
    ...((isAdmin || user?.role === "doctor") ? [{ id: "pin", label: t.pinLockTab }] : []),
    ...(isAdmin ? [{ id: "audit",      label: t.auditLogTab }]   : []),
    ...(isAdmin ? [{ id: "categories", label: "Categories" }]    : []),
    { id: "system",   label: t.systemTab },
  ];

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 2 }}>{t.settingsTitle}</h1>
        <p style={{ color: "#64748B", fontSize: 13 }}>{t.settingsSec}</p>
      </div>

      <div className="tabs">
        {TABS.map(({ id, label }) => (
          <button key={id} className={`tab ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ── PASSWORDS ─────────────────────────────────────────────────────── */}
      {tab === "passwords" && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div style={{ fontWeight: 700, fontSize: 15 }}>{t.changePassword}</div>
            </div>
            <div className="card-body">
              {error   && <div className="alert alert-red">{error}</div>}
              {success && <div className="alert alert-green">{success}</div>}

              {/* Role selector — admin sees all; others see only their own */}
              <div className="form-group">
                <label className="form-label">{t.selectRole}</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {ALL_ROLES.filter(r => isAdmin || r.id === user?.role).map(r => (
                    <button key={r.id} onClick={() => setTarget(r.id)} style={{
                      flex: 1, minWidth: 100, padding: "10px 8px", borderRadius: 10, border: "1px solid",
                      borderColor: target === r.id ? "#4F46E5" : "#E4E8F0",
                      background: target === r.id ? "#EEF2FF" : "#F8F9FC",
                      cursor: "pointer", textAlign: "center",
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: target === r.id ? "#4F46E5" : "#475569" }}>{r.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">{t.newPassword}</label>
                  <div style={{ position: "relative" }}>
                    <input className="input" type={showPass ? "text" : "password"} value={newPass} onChange={e => setNew(e.target.value)} placeholder="Min 8 characters" style={{ paddingRight: 38 }} />
                    <span onClick={() => setShowPass(p => !p)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center" }}>
                      {showPass
                        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{t.confirmPassword}</label>
                  <div style={{ position: "relative" }}>
                    <input className="input" type={showPass ? "text" : "password"} value={confirm} onChange={e => setCon(e.target.value)} placeholder="Repeat password" style={{ paddingRight: 38 }} />
                  </div>
                </div>
              </div>
              <button className="btn btn-primary" onClick={handleChange}>{t.updatePassword}</button>
            </div>
          </div>

          {/* Admin Recovery Email */}
          {isAdmin && (
            <div className="card" style={{ marginBottom: 16, borderTop: "2px solid #4F46E5" }}>
              <div className="card-header">
                <div style={{ fontWeight: 700, fontSize: 15 }}>{t.adminRecoveryEmail}</div>
                <span className="badge" style={{ background: "#EEF2FF", color: "#4F46E5", fontSize: 10 }}>{t.forPasswordReset}</span>
              </div>
              <div className="card-body">
                {adminEmailErr && <div className="alert alert-red">{adminEmailErr}</div>}
                {adminEmailMsg && <div className="alert alert-green">{adminEmailMsg}</div>}
                <div className="alert alert-blue" style={{ marginBottom: 14 }}>
                  This email is used to send a password reset link if you forget your admin password. Keep it up to date.
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">{t.recoveryEmail}</label>
                    <input
                      className="input"
                      type="email"
                      value={adminEmail}
                      onChange={e => setAdminEmail(e.target.value)}
                      placeholder="your@gmail.com"
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    disabled={emailLoading || !adminEmail}
                    onClick={async () => {
                      setEmailLoading(true); setAdminEmailErr(""); setAdminEmailMsg("");
                      try {
                        const token = localStorage.getItem("emr_token");
                        const res = await fetch(
                          (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/auth/save-admin-email",
                          { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify({ email: adminEmail }) }
                        );
                        const data = await res.json();
                        if (res.ok) setAdminEmailMsg("Recovery email saved successfully.");
                        else setAdminEmailErr(data.detail || "Failed to save.");
                      } catch (e) { setAdminEmailErr(e.message); }
                      finally { setEmailLoading(false); }
                    }}
                  >{emailLoading ? t.saving : t.saveEmail}</button>
                </div>
              </div>
            </div>
          )}

          {!mfaEnabled && (
            <div className="card" style={{ borderTop: "2px solid #DC2626" }}>
              <div className="card-header">
                <div style={{ fontWeight: 700, fontSize: 15 }}>Emergency Admin Reset</div>
                <span className="badge badge-red">Emergency Only</span>
              </div>
              <div className="card-body">
                {emgErr  && <div className="alert alert-red">{emgErr}</div>}
                {emgSucc && <div className="alert alert-green">{emgSucc}</div>}
                <div className="alert alert-amber">
                  Use master backup code to reset Admin password if locked out. This option is automatically disabled once two-factor authentication is enabled.
                </div>
                <div className="form-group">
                  <label className="form-label">Master Backup Code</label>
                  <input className="input" type="password" value={emgCode} onChange={e => setEmgCode(e.target.value)} placeholder="Enter master code" />
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">New Admin Password</label>
                    <input className="input" type="password" value={emgNew} onChange={e => setEmgNew(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm</label>
                    <input className="input" type="password" value={emgCon} onChange={e => setEmgCon(e.target.value)} />
                  </div>
                </div>
                <button className="btn btn-danger" onClick={handleEmergency}>Reset Admin Password</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MASTER CODE (admin only) ──────────────────────────────────────── */}
      {tab === "mfa" && isAdmin && (
        <div className="card">
          <div className="card-header">
            <div style={{ fontWeight: 700, fontSize: 15 }}>Two-factor authentication</div>
            {!mfaStatusLoading && (
              <span className={`badge ${mfaEnabled ? "badge-green" : "badge-gray"}`}>
                {mfaEnabled ? "Enabled" : "Disabled"}
              </span>
            )}
          </div>
          <div className="card-body">
            {mfaStatusLoading ? (
              <div style={{ color: "#94A3B8", fontSize: 13 }}>Loading...</div>
            ) : mfaSetupStep === "backup-codes" ? (
              <div>
                <div className="alert alert-green" style={{ marginBottom: 14 }}>
                  Two-factor authentication is enabled.
                </div>
                <div style={{ border: "1.5px solid #FB923C", background: "#FFF7ED", borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#C2410C", marginBottom: 6 }}>
                    Save these backup codes now
                  </div>
                  <p style={{ fontSize: 12, color: "#7C2D12", marginBottom: 12 }}>
                    Each code works once to log in or reset your password if you lose access to your authenticator app. They are shown only this one time — store them somewhere safe.
                  </p>
                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
                    background: "#fff", border: "1px solid #FED7AA", borderRadius: 8, padding: 12,
                  }}>
                    {newBackupCodes.map(code => (
                      <div key={code} style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: 1 }}>{code}</div>
                    ))}
                  </div>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: 10 }}
                    onClick={() => navigator.clipboard.writeText(newBackupCodes.join("\n"))}
                  >
                    Copy all codes
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <input type="checkbox" id="codes-ack" checked={codesAcknowledged} onChange={e => setCodesAcknowledged(e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: "#4F46E5", cursor: "pointer" }} />
                  <label htmlFor="codes-ack" style={{ fontSize: 12, color: "#64748B", cursor: "pointer" }}>
                    I have saved these backup codes somewhere safe
                  </label>
                </div>
                <button className="btn btn-primary" disabled={!codesAcknowledged} onClick={finishMfaSetup}>
                  Done
                </button>
              </div>
            ) : mfaEnabled ? (
              <div>
                <div className="alert alert-green" style={{ marginBottom: 16 }}>
                  Two-factor authentication is active. Every admin login requires a code from your authenticator app.
                </div>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  border: "1px solid #E2E8F0", borderRadius: 10, padding: "12px 14px", marginBottom: 16,
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>Backup codes</div>
                    <div style={{ fontSize: 11, color: mfaBackupRemaining <= 2 ? "#DC2626" : "#64748B", marginTop: 2 }}>
                      {mfaBackupRemaining} of 8 remaining
                      {mfaBackupRemaining <= 2 && " — running low, regenerate soon"}
                    </div>
                  </div>
                  {!showRegenBackup && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowRegenBackup(true)}>Regenerate</button>
                  )}
                </div>
                {showRegenBackup && (
                  <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 16, marginBottom: 16, background: "#F8FAFC" }}>
                    {regenErr && <div className="alert alert-red">{regenErr}</div>}
                    <p style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>
                      Generating new codes invalidates any unused codes from before. Enter your current authenticator code to confirm.
                    </p>
                    <div className="form-group">
                      <label className="form-label">6-digit code</label>
                      <input className="input" value={regenCode}
                        onChange={e => setRegenCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000" inputMode="numeric" maxLength={6}
                        style={{ fontSize: 18, letterSpacing: 4, textAlign: "center", fontFamily: "monospace" }} />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-ghost" onClick={() => { setShowRegenBackup(false); setRegenCode(""); setRegenErr(""); }}>Cancel</button>
                      <button className="btn btn-primary" disabled={regenLoading || regenCode.length !== 6} onClick={handleRegenerateBackupCodes}>
                        {regenLoading ? "Generating..." : "Generate new codes"}
                      </button>
                    </div>
                  </div>
                )}
                {!showDisableMfa ? (
                  <button className="btn btn-danger" onClick={() => setShowDisableMfa(true)}>Disable two-factor authentication</button>
                ) : (
                  <div style={{ border: "1px solid #FECACA", borderRadius: 10, padding: 16, background: "#FEF2F2" }}>
                    {mfaDisableErr && <div className="alert alert-red">{mfaDisableErr}</div>}
                    <p style={{ fontSize: 13, color: "#64748B", marginBottom: 12 }}>
                      Enter your current authenticator code to confirm disabling two-factor authentication.
                    </p>
                    <div className="form-group">
                      <label className="form-label">6-digit code</label>
                      <input className="input" value={mfaDisableCode}
                        onChange={e => setMfaDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="000000" inputMode="numeric" maxLength={6}
                        style={{ fontSize: 18, letterSpacing: 4, textAlign: "center", fontFamily: "monospace" }} />
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-ghost" onClick={() => { setShowDisableMfa(false); setMfaDisableCode(""); setMfaDisableErr(""); }}>Cancel</button>
                      <button className="btn btn-danger" disabled={mfaDisableLoading || mfaDisableCode.length !== 6} onClick={handleMfaDisable}>
                        {mfaDisableLoading ? "Disabling..." : "Confirm disable"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : !mfaSetupStep ? (
              <div>
                <div className="alert alert-amber" style={{ marginBottom: 16 }}>
                  Two-factor authentication is currently disabled. Enable it to require a 6-digit code from Google Authenticator or a similar app on every admin login, and to unlock the authenticator-based password reset option.
                </div>
                <button className="btn btn-primary" onClick={startMfaSetup} disabled={mfaSetupLoading}>
                  {mfaSetupLoading ? "Starting..." : "Set up two-factor authentication"}
                </button>
              </div>
            ) : mfaSetupStep === "qr" && (
              <div>
                {mfaSetupErr && <div className="alert alert-red">{mfaSetupErr}</div>}
                <p style={{ fontSize: 13, color: "#64748B", marginBottom: 14 }}>
                  Scan this QR code with Google Authenticator, Authy, or any compatible app, then enter the 6-digit code it shows.
                </p>
                {mfaQrCode && (
                  <div style={{ textAlign: "center", marginBottom: 16 }}>
                    <img src={mfaQrCode} alt="MFA QR code" style={{ width: 200, height: 200, border: "1px solid #E2E8F0", borderRadius: 10 }} />
                  </div>
                )}
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "10px 14px", marginBottom: 16, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 4 }}>Can't scan? Enter this code manually:</div>
                  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>{mfaSecretText}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Enter the code from your app</label>
                  <input className="input" value={mfaSetupCode}
                    onChange={e => setMfaSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000" autoFocus inputMode="numeric" maxLength={6}
                    style={{ fontSize: 18, letterSpacing: 4, textAlign: "center", fontFamily: "monospace" }} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-ghost" onClick={cancelMfaSetup}>Cancel</button>
                  <button className="btn btn-primary" disabled={mfaSetupLoading || mfaSetupCode.length !== 6} onClick={confirmMfaSetup}>
                    {mfaSetupLoading ? "Verifying..." : "Verify and enable"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PIN LOCK ─────────────────────────────────────────────────────── */}
      {tab === "pin" && (user?.role === "admin" || user?.role === "doctor") && (
        <div className="card">
          <div className="card-header">
            <div style={{ fontWeight: 700, fontSize: 15 }}>PIN Lock</div>
            <span className="badge" style={{ background: hasPin ? "#DCFCE7" : "#F1F5F9", color: hasPin ? "#16A34A" : "#64748B", fontSize: 10 }}>
              {hasPin ? "PIN Active" : "Not Set"}
            </span>
          </div>
          <div className="card-body">
            {pinErr  && <div className="alert alert-red">{pinErr}</div>}
            {pinSucc && <div className="alert alert-green">{pinSucc}</div>}
            <div className="alert alert-blue" style={{ marginBottom: 16 }}>
              When your session is idle ({user?.role === "admin" ? "30 minutes" : "60 minutes"}), a PIN lock screen will appear instead of signing you out. Enter your PIN to resume instantly.
            </div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">New PIN (4 digits)</label>
                <input className="input" type="password" inputMode="numeric" maxLength={4}
                  value={pin1} onChange={e => setPin1(e.target.value.replace(/\D/g,"").slice(0,4))}
                  placeholder="e.g. 1234" />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm PIN</label>
                <input className="input" type="password" inputMode="numeric" maxLength={4}
                  value={pin2} onChange={e => setPin2(e.target.value.replace(/\D/g,"").slice(0,4))}
                  placeholder="Repeat PIN" />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" disabled={pinLoading || pin1.length !== 4 || pin2.length !== 4}
                onClick={async () => {
                  setPinErr(""); setPinSucc("");
                  if (pin1 !== pin2) return setPinErr("PINs do not match.");
                  setPinLoading(true);
                  try {
                    const token = localStorage.getItem("emr_token");
                    const res = await fetch(
                      (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/auth/save-pin",
                      { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify({ pin: pin1, role: user.role }) }
                    );
                    const data = await res.json();
                    if (res.ok) { setPinSucc("PIN saved. You will be prompted on next idle."); setHasPin(true); setPin1(""); setPin2(""); }
                    else setPinErr(data.detail || "Failed to save PIN.");
                  } catch (e) { setPinErr(e.message); }
                  finally { setPinLoading(false); }
                }}>
                {pinLoading ? "Saving..." : (hasPin ? "Update PIN" : "Set PIN")}
              </button>
              {hasPin && (
                <button className="btn btn-danger btn-sm" onClick={async () => {
                  setPinErr(""); setPinSucc("");
                  try {
                    const token = localStorage.getItem("emr_token");
                    const res = await fetch(
                      (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + `/auth/pin/${user.role}`,
                      { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } }
                    );
                    if (res.ok) { setPinSucc("PIN cleared. No PIN lock will be shown."); setHasPin(false); }
                  } catch (e) { setPinErr(e.message); }
                }}>Clear PIN</button>
              )}
            </div>
            {isAdmin && user?.role === "admin" && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #F1F5F9" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", marginBottom: 8 }}>Doctor PIN (Admin)</div>
                <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>As admin you can also clear the doctor PIN if they are locked out.</div>
                <button className="btn btn-ghost btn-sm" onClick={async () => {
                  try {
                    const token = localStorage.getItem("emr_token");
                    const res = await fetch(
                      (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/auth/pin/doctor",
                      { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } }
                    );
                    if (res.ok) setPinSucc("Doctor PIN cleared.");
                  } catch {}
                }}>Clear Doctor PIN</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── AUDIT LOG (admin only) ────────────────────────────────────────── */}
      {tab === "audit" && isAdmin && (() => {
        const PERIODS = [
          { id:"today", label:"Today" },
          { id:"7d",    label:"7 days" },
          { id:"30d",   label:"30 days" },
          { id:"all",   label:"All time" },
        ];

        const loadAudit = (period) => {
          setAuditLoading(true);
          getAuditLog(500, period).then(data => {
            setAuditLog(Array.isArray(data) ? data : []);
            setAuditLoading(false);
          }).catch(() => setAuditLoading(false));
        };

        const switchPeriod = (p) => { setAuditPeriod(p); loadAudit(p); };

        const extractPatient = (detail) => {
          if (!detail) return null;
          const m = detail.match(/for (NI-\d+)/);
          return m ? m[1] : null;
        };

        const ACTION_STYLE = {
          "login":                { bg:"#DBEAFE", color:"#1E40AF",  dot:"#2563EB",  label:"login",             Icon: LogIn },
          "login_failed":         { bg:"#FEE2E2", color:"#991B1B",  dot:"#DC2626",  label:"login failed",      Icon: ShieldX },
          "login_rate_limited":   { bg:"#FEE2E2", color:"#991B1B",  dot:"#DC2626",  label:"rate limited",      Icon: ShieldX },
          "report_uploaded":      { bg:"#DCFCE7", color:"#166534",  dot:"#16A34A",  label:"report uploaded",   Icon: FileUp },
          "report_values_edited": { bg:"#DBEAFE", color:"#1E40AF",  dot:"#2563EB",  label:"values edited",     Icon: FilePen },
          "report_deleted":       { bg:"#FEE2E2", color:"#991B1B",  dot:"#DC2626",  label:"report deleted",    Icon: Trash2 },
          "patient_registered":   { bg:"#DCFCE7", color:"#166534",  dot:"#16A34A",  label:"registered",        Icon: UserPlus },
          "patient_deleted":      { bg:"#FEE2E2", color:"#991B1B",  dot:"#DC2626",  label:"patient deleted",   Icon: UserMinus },
          "patient_updated":      { bg:"#DBEAFE", color:"#1E40AF",  dot:"#2563EB",  label:"patient updated",   Icon: UserCog },
          "excel_import":         { bg:"#DCFCE7", color:"#166534",  dot:"#16A34A",  label:"excel import",      Icon: FileSpreadsheet },
          "excel_bulk_import":    { bg:"#DCFCE7", color:"#166534",  dot:"#16A34A",  label:"bulk import",       Icon: FileSpreadsheet },
          "excel_batch_import":   { bg:"#DCFCE7", color:"#166534",  dot:"#16A34A",  label:"batch import",      Icon: FileSpreadsheet },
          "change_password":      { bg:"#FEF3C7", color:"#92400E",  dot:"#D97706",  label:"password changed",  Icon: KeyRound },
          "emergency_reset":      { bg:"#FEE2E2", color:"#991B1B",  dot:"#DC2626",  label:"emergency reset",   Icon: KeyRound },
          "master_code_updated":  { bg:"#FEF3C7", color:"#92400E",  dot:"#D97706",  label:"master code",       Icon: KeyRound },
          "restore_completed":    { bg:"#DCFCE7", color:"#166534",  dot:"#16A34A",  label:"restore",           Icon: RotateCcw },
          "pin_set":              { bg:"#EDE9FE", color:"#4C1D95",  dot:"#7C3AED",  label:"pin set",           Icon: Lock },
          "pin_cleared":          { bg:"#F1F5F9", color:"#475569",  dot:"#94A3B8",  label:"pin cleared",       Icon: Unlock },
          "report_saved":         { bg:"#DCFCE7", color:"#166534",  dot:"#16A34A",  label:"report saved",      Icon: FileUp },
        };
        const getStyle = (a) => ACTION_STYLE[a] || { bg:"#F1F5F9", color:"#475569", dot:"#94A3B8", label: a?.replace(/_/g," ") || "—", Icon: RefreshCw };

        const ROLE_STYLE = {
          admin:        { bg:"#FCEBEB", color:"#791F1F" },
          doctor:       { bg:"#E6F1FB", color:"#0C447C" },
          technician:   { bg:"#EAF3DE", color:"#27500A" },
          receptionist: { bg:"#EEEDFE", color:"#26215C" },
        };
        const getRoleStyle = (role) => ROLE_STYLE[role] || { bg:"#F1EFE8", color:"#444441" };

        const filtered = auditLog.filter(e => {
          if (auditRole !== "all" && e.role !== auditRole) return false;
          if (auditAction !== "all" && e.action !== auditAction) return false;
          if (auditSearch) {
            const s = auditSearch.toLowerCase();
            if (!(e.detail||"").toLowerCase().includes(s) &&
                !(e.role||"").toLowerCase().includes(s) &&
                !(e.username||"").toLowerCase().includes(s) &&
                !(e.action||"").toLowerCase().includes(s)) return false;
          }
          return true;
        });

        const counts = {
          total:   filtered.length,
          logins:  filtered.filter(e=>e.action==="login").length,
          failed:  filtered.filter(e=>e.action==="login_failed").length,
          uploads: filtered.filter(e=>["report_uploaded","excel_import","excel_bulk_import","excel_batch_import"].includes(e.action)).length,
          edits:   filtered.filter(e=>e.action==="report_values_edited").length,
          deleted: filtered.filter(e=>["report_deleted","patient_deleted"].includes(e.action)).length,
        };

        const periodLabel = PERIODS.find(p=>p.id===auditPeriod)?.label || "Today";
        const actionTypes = [...new Set(auditLog.map(e=>e.action).filter(Boolean))].sort();

        return (
          <div style={{ maxWidth:"100%" }}>
            {/* IP Security */}
            <div style={{ marginBottom:24, paddingBottom:24, borderBottom:"1px solid #E2E8F0" }}>
              <div style={{ fontWeight:600, fontSize:14, color:"#0F172A", marginBottom:4 }}>IP Security</div>
              <div style={{ fontSize:12, color:"#64748B", marginBottom:12 }}>Monitor login attempts and block suspicious IP addresses.</div>
              {blockMsg && <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:12, fontSize:12, background:blockMsg.startsWith("✅")?"#F0FDF4":"#FEF2F2", color:blockMsg.startsWith("✅")?"#166534":"#991B1B" }}>{blockMsg}</div>}
              {blockedIPs.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#DC2626", marginBottom:8, textTransform:"uppercase" }}>🚫 Blocked IPs ({blockedIPs.length})</div>
                  {blockedIPs.map(b => (
                    <div key={b.ip} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:"#FEF2F2", borderRadius:8, border:"1px solid #FECACA", marginBottom:4 }}>
                      <div>
                        <span style={{ fontFamily:"monospace", fontWeight:600, fontSize:13, color:"#DC2626" }}>{b.ip}</span>
                        {b.reason && <span style={{ fontSize:11, color:"#94A3B8", marginLeft:8 }}>{b.reason}</span>}
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{ color:"#16A34A", fontSize:11 }} onClick={async () => {
                        try { await authAPI.unblockIP(b.ip); setBlockedIPs(p=>p.filter(x=>x.ip!==b.ip)); setLoginAttempts(p=>p.map(a=>a.ip===b.ip?{...a,is_blocked:false}:a)); setBlockMsg("✅ "+b.ip+" unblocked"); setTimeout(()=>setBlockMsg(""),3000); } catch(e){setBlockMsg("Error: "+e.message);}
                      }}>✓ Unblock</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize:11, fontWeight:700, color:"#475569", marginBottom:8, textTransform:"uppercase" }}>Login Attempts by IP</div>
              {ipLoading ? <div style={{ fontSize:12, color:"#94A3B8" }}>Loading...</div> : loginAttempts.length === 0 ? <div style={{ fontSize:12, color:"#94A3B8" }}>No attempts recorded.</div> : (
                <div style={{ border:"1px solid #E2E8F0", borderRadius:8, overflow:"hidden" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1.2fr 55px 65px 55px 155px 85px", background:"#F8FAFC", padding:"8px 12px", borderBottom:"1px solid #E2E8F0" }}>
                    {["IP","Total","Success","Failed","Last Seen","Action"].map(h=><div key={h} style={{ fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase" }}>{h}</div>)}
                  </div>
                  {loginAttempts.map(a=>(
                    <div key={a.ip} style={{ display:"grid", gridTemplateColumns:"1.2fr 55px 65px 55px 155px 85px", padding:"9px 12px", borderBottom:"1px solid #F1F5F9", background:a.is_blocked?"#FEF2F2":a.failures>10?"#FFFBEB":"#fff", alignItems:"center" }}>
                      <div style={{ fontFamily:"monospace", fontSize:12, fontWeight:600, color:a.is_blocked?"#DC2626":"#0F172A" }}>{a.is_blocked?"🚫 ":""}{a.ip}</div>
                      <div style={{ fontSize:12, color:"#475569" }}>{a.total}</div>
                      <div style={{ fontSize:12, color:"#16A34A" }}>{a.successes}</div>
                      <div style={{ fontSize:12, color:a.failures>5?"#DC2626":"#475569" }}>{a.failures}</div>
                      <div style={{ fontSize:11, color:"#94A3B8" }}>{a.last_attempt ? new Date(a.last_attempt).toLocaleString("en-GB") : "—"}</div>
                      <div>{a.is_blocked ? (
                        <button className="btn btn-ghost btn-sm" style={{ fontSize:11, color:"#16A34A" }} onClick={async()=>{
                          try{await authAPI.unblockIP(a.ip);setLoginAttempts(p=>p.map(x=>x.ip===a.ip?{...x,is_blocked:false}:x));setBlockedIPs(p=>p.filter(x=>x.ip!==a.ip));setBlockMsg("✅ "+a.ip+" unblocked");setTimeout(()=>setBlockMsg(""),3000);}catch(e){setBlockMsg("Error: "+e.message);}
                        }}>✓ Unblock</button>
                      ) : (
                        <button className="btn btn-danger btn-sm" style={{ fontSize:11 }} onClick={async()=>{
                          const reason=window.prompt("Reason for blocking "+a.ip+":","Suspicious activity");
                          if(reason===null)return;
                          try{await authAPI.blockIP(a.ip,reason);setLoginAttempts(p=>p.map(x=>x.ip===a.ip?{...x,is_blocked:true}:x));setBlockedIPs(p=>[...p,{ip:a.ip,reason,blocked_at:new Date().toISOString()}]);setBlockMsg("✅ "+a.ip+" blocked");setTimeout(()=>setBlockMsg(""),3000);}catch(e){setBlockMsg("Error: "+e.message);}
                        }}>🚫 Block</button>
                      )}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:15, color:"#0F172A" }}>Audit log</div>
                <div style={{ fontSize:12, color:"#64748B", marginTop:2 }}>{filtered.length} events · {periodLabel.toLowerCase()} · times shown in BD (UTC+6)</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => loadAudit(auditPeriod)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                Refresh
              </button>
            </div>

            {/* Stats */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8, marginBottom:16 }}>
              {[
                { label:"Total",     value:counts.total,   sub:periodLabel.toLowerCase(), color:"#0F172A",  bg:"#F8FAFC",  border:"#E2E8F0" },
                { label:"Logins",    value:counts.logins,  sub:`${counts.failed} failed`,  color:"#185FA5",  bg:"#EFF6FF",  border:"#BFDBFE" },
                { label:"Uploads",   value:counts.uploads, sub:"added",                    color:"#166534",  bg:"#F0FDF4",  border:"#BBF7D0" },
                { label:"Edits",     value:counts.edits,   sub:"value changes",            color:"#185FA5",  bg:"#EFF6FF",  border:"#BFDBFE" },
                { label:"Deletions", value:counts.deleted, sub:"removed",                  color:"#991B1B",  bg:"#FEF2F2",  border:"#FECACA" },
              ].map(s => (
                <div key={s.label} style={{ background:s.bg, borderRadius:10, padding:"10px 12px", border:`0.5px solid ${s.border}` }}>
                  <div style={{ fontSize:11, color:"#94A3B8", marginBottom:3 }}>{s.label}</div>
                  <div style={{ fontSize:20, fontWeight:600, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:10, color:"#94A3B8", marginTop:1 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Period tabs */}
            <div style={{ display:"flex", gap:4, background:"#F1F5F9", borderRadius:10, padding:3, marginBottom:12, width:"fit-content" }}>
              {PERIODS.map(p => (
                <button key={p.id} onClick={() => switchPeriod(p.id)} style={{
                  padding:"5px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:12,
                  background: auditPeriod===p.id ? "#fff" : "transparent",
                  color: auditPeriod===p.id ? "#0F172A" : "#64748B",
                  fontWeight: auditPeriod===p.id ? 500 : 400,
                  boxShadow: auditPeriod===p.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}>{p.label}</button>
              ))}
            </div>

            {/* Filter chips */}
            <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
              {[
                { label:"All",    value:"all" },
                { label:"Login",  value:"login" },
                { label:"Upload", value:"report_uploaded" },
                { label:"Delete", value:"patient_deleted" },
                { label:"Failed", value:"login_failed" },
              ].map(chip => (
                <button
                  key={chip.value}
                  onClick={() => setAuditAction(chip.value)}
                  style={{
                    fontSize:12, padding:"5px 14px", borderRadius:20, border:"0.5px solid",
                    cursor:"pointer", fontWeight: auditAction === chip.value ? 500 : 400,
                    background: auditAction === chip.value ? "#4F46E5" : "#fff",
                    color:      auditAction === chip.value ? "#fff"    : "#475569",
                    borderColor:auditAction === chip.value ? "#4F46E5" : "#E2E8F0",
                    transition:"all 0.15s",
                  }}
                >{chip.label}</button>
              ))}
            </div>

            {/* Role + search row */}
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              <select value={auditRole} onChange={e=>setAuditRole(e.target.value)}
                style={{ fontSize:12, padding:"6px 10px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", color:"#0F172A" }}>
                <option value="all">All roles</option>
                {["admin","doctor","technician","receptionist"].map(r=><option key={r} value={r}>{r}</option>)}
              </select>
              <input value={auditSearch} onChange={e=>setAuditSearch(e.target.value)}
                placeholder="Search patient, EMR ID, username..."
                style={{ fontSize:12, padding:"6px 10px", borderRadius:8, border:"1px solid #E2E8F0", flex:1, minWidth:200, outline:"none" }} />
            </div>

            {/* Timeline */}
            {auditLoading ? (
              <div className="empty-state"><p>Loading...</p></div>
            ) : filtered.length === 0 ? (
              <div className="empty-state"><p>No matching events</p></div>
            ) : (
              <div style={{ background:"#fff", border:"0.5px solid #E2E8F0", borderRadius:12, padding:"0 16px" }}>
                {filtered.map((entry, i) => {
                  const s         = getStyle(entry.action);
                  const rs        = getRoleStyle(entry.role);
                  const patientId = extractPatient(entry.detail);
                  const initials  = (entry.username || entry.role || "??").substring(0,2).toUpperCase();
                  const isLast    = i === filtered.length - 1;
                  return (
                    <div key={entry.id || i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"10px 0", borderBottom: isLast ? "none" : "0.5px solid #F1F5F9" }}>
                      {/* Icon box */}
                      <div style={{ width:32, height:32, borderRadius:8, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {s.Icon && <s.Icon size={14} color={s.color} />}
                      </div>
                      {/* Content */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:3 }}>
                          <span style={{ fontSize:12, fontWeight:600, color:s.color }}>{s.label}</span>
                          {(entry.action === "login_failed" || entry.action === "login_rate_limited") && (
                            <span style={{ fontSize:10, fontWeight:600, background:"#FEE2E2", color:"#991B1B", padding:"1px 7px", borderRadius:4 }}>security alert</span>
                          )}
                          {patientId && <span style={{ fontFamily:"monospace", fontSize:10, background:"#F1F5F9", padding:"1px 6px", borderRadius:4, color:"#0F172A" }}>{patientId}</span>}
                        </div>
                        <div style={{ fontSize:11, color:"#64748B", lineHeight:1.5, marginBottom:2 }}>{entry.detail || "—"}</div>
                        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                          <div style={{ width:16, height:16, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:600, background:entry.role ? rs.bg : "#F1F5F9", color:entry.role ? rs.color : "#64748B", flexShrink:0 }}>{initials}</div>
                          <span style={{ fontSize:10, color:"#94A3B8" }}>{entry.username || "—"}{entry.role ? ` · ${entry.role}` : ""}</span>
                        </div>
                      </div>
                      {/* Timestamp BD (UTC+6) */}
                      <div style={{ fontSize:11, color:"#64748B", textAlign:"right", whiteSpace:"nowrap", flexShrink:0 }}>
                        {entry.timestamp ? (() => {
                          const d = new Date(entry.timestamp.endsWith("Z") ? entry.timestamp : entry.timestamp + "Z");
                          const bd = new Date(d.getTime() + 6 * 60 * 60 * 1000);
                          const h  = bd.getUTCHours();
                          const m  = String(bd.getUTCMinutes()).padStart(2,"0");
                          const ampm = h >= 12 ? "PM" : "AM";
                          const h12 = h % 12 || 12;
                          return <>{h12}:{m} {ampm}</>;
                        })() : "—"}<br/>
                        <span style={{ fontSize:10, color:"#94A3B8" }}>
                          {entry.timestamp ? (() => {
                            const d = new Date(entry.timestamp.endsWith("Z") ? entry.timestamp : entry.timestamp + "Z");
                            const bd = new Date(d.getTime() + 6 * 60 * 60 * 1000);
                            return `${String(bd.getUTCDate()).padStart(2,"0")}/${String(bd.getUTCMonth()+1).padStart(2,"0")}`;
                          })() : ""}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, fontSize:12, color:"#94A3B8" }}>
              <span>{filtered.length} of {auditLog.length} events</span>
              <button className="btn btn-ghost btn-sm" onClick={() => loadAudit(auditPeriod)}>Load more</button>
            </div>
          </div>
        );
      })()}

            {/* ── CATEGORIES ──────────────────────────────────────────────────────── */}
      {tab === "categories" && isAdmin && <CategoriesTab />}

            {/* ── SYSTEM ────────────────────────────────────────────────────────── */}
      {tab === "system" && (
        <div>
          {isAdmin && (
            <div className="card" style={{ marginBottom: 16, borderTop: maintenanceOn ? "3px solid #DC2626" : "3px solid #16A34A" }}>
              <div className="card-header">
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>Maintenance Mode</div>
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    {maintenanceOn
                      ? "⚠ System is currently under maintenance — all users are blocked"
                      : "✓ System is live and accessible to all users"}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 48, height: 26, borderRadius: 99, cursor: maintenanceLoading ? "not-allowed" : "pointer",
                    background: maintenanceOn ? "#DC2626" : "#E4E8F0",
                    position: "relative", transition: "background 0.2s",
                    opacity: maintenanceLoading ? 0.6 : 1
                  }} onClick={async () => {
                    if (maintenanceLoading) return;
                    setMaintenanceLoading(true);
                    setMaintenanceMsg("");
                    try {
                      const token = localStorage.getItem("emr_token");
                      const res = await fetch(
                        (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app") + "/backup/maintenance",
                        { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify({ enabled: !maintenanceOn }) }
                      );
                      const data = await res.json();
                      setMaintenanceOn(data.maintenance);
                      setMaintenanceMsg(data.message || "Updated");
                    } catch (e) {
                      setMaintenanceMsg("Failed: " + e.message);
                    } finally {
                      setMaintenanceLoading(false);
                    }
                  }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: "50%", background: "#fff",
                      position: "absolute", top: 3,
                      left: maintenanceOn ? 25 : 3,
                      transition: "left 0.2s",
                      boxShadow: "0 1px 4px rgba(0,0,0,.2)"
                    }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: maintenanceOn ? "#DC2626" : "#16A34A" }}>
                    {maintenanceLoading ? "Updating..." : maintenanceOn ? "ON" : "OFF"}
                  </span>
                </div>
              </div>
              {maintenanceMsg && (
                <div style={{ padding: "8px 20px", fontSize: 12, color: maintenanceOn ? "#DC2626" : "#16A34A", fontWeight: 600 }}>
                  {maintenanceMsg}
                </div>
              )}
            </div>
          )}
          <div className="card">
          <div className="card-body">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
              {[
                ["Institution",     "Nashman Institute"],
                ["System",          "Nashman EMR v2.0"],
                ["Frontend",        "React"],
                ["Backend",         "Python FastAPI"],
                ["Database",        "SQLite (local)"],
                ["AI Engine",       "Claude Sonnet/Haiku (Anthropic)"],
                ["PDF Extraction",  "pdfplumber + Claude Vision"],
                ["Scanned PDFs",    "pdf2image + pypdfium2 + Claude Vision"],
                ["Imaging",         "X-Ray, CT, MRI, Ultrasound"],
                ["API Docs",        (window.location.hostname === "localhost" ? "http://localhost:8000/docs" : "https://nashman-prod-production-c773.up.railway.app/docs")],
              ].map(([k, v]) => (
                <div key={k} style={{ padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px" }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A", marginTop: 2 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}

// ── Categories Management Tab ─────────────────────────────────────────────────
function CategoryPill({ name, isCustom, onDelete, onRename, saving }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(name);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, background: isCustom ? "#F0FDF4" : "#EEF2FF", borderRadius: 99, padding: "3px 6px 3px 12px", border: isCustom ? "1px solid #BBF7D0" : "none" }}>
      {editing ? (
        <>
          <input value={val} onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { onRename(name, val); setEditing(false); } if (e.key === "Escape") { setEditing(false); setVal(name); } }}
            style={{ fontSize: 12, border: "none", outline: "none", background: "transparent", width: Math.max(60, val.length * 8), color: "#0F172A" }} autoFocus />
          <button onClick={() => { onRename(name, val); setEditing(false); }} disabled={saving}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#10B981", fontSize: 13, padding: "0 2px" }}>✓</button>
          <button onClick={() => { setEditing(false); setVal(name); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 13, padding: "0 2px" }}>✕</button>
        </>
      ) : (
        <>
          <span style={{ color: isCustom ? "#15803D" : "#4F46E5", fontSize: 13, fontWeight: 500 }}>{name}</span>
          <button onClick={() => { setEditing(true); setVal(name); }} disabled={saving}
            title={`Rename ${name}`}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 11, padding: "0 2px", lineHeight: 1 }}>✎</button>
          <button onClick={() => onDelete()} disabled={saving}
            title={`Delete ${name}`}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14, lineHeight: 1, padding: "0 2px" }}>×</button>
        </>
      )}
    </div>
  );
}

function CategoriesTab() {
  const { user } = useApp();
  const canAction = user?.role === "admin";
  const PREDEFINED = [
    "Hematology","Hormone","Immunology","Biochemical","Lipid Profile",
    "Liver Function","Kidney Function","Coagulation","Urinalysis",
    "Serology","Microbiology","Diabetes","Other",
    "Cardiac Markers","Tumor Markers","Pathology","Stool Analysis"
  ];

  const REPORT_SUBTYPES = {
    "Hematology": ["CBC / Complete Blood Count","PBF / Blood Film Study","Semen Analysis","ESR","Haemoglobin (Hb)","Blood Grouping & Rh Factor","BT / CT","Circulating Eosinophil Count"],
    "Biochemical": ["Blood Sugar / RBS / FBS","Electrolytes","Ferritin","Vitamin D","AMH","Urine for Protein"],
    "Diabetes": ["FBS / Fasting Blood Sugar","HbA1c","OGTT","FBS + 2HABF Combined"],
    "Lipid Profile": ["Lipid Profile"],
    "Liver Function": ["Full LFT Panel","Serum Bilirubin (Direct & Indirect)","A/G Ratio / Serum Proteins"],
    "Kidney Function": ["Full KFT Panel","eGFR","ACR / Urine Albumin","Urine for Protein"],
    "Hormone": ["Thyroid Panel (T3/T4/TSH/FT3/FT4)","Reproductive Hormones","Beta-hCG"],
    "Immunology": ["Total IgE","Anti-CCP"],
    "Serology": ["CRP + RA Test (Qualitative)","ASO Titre","ASO + CRP + RA Combined","CRP Quantitative / Hs-CRP","Dengue NS1+IgM+IgG","HBsAg + Anti-HCV + HIV + VDRL Screening","HBsAg ELISA","Cross Matching","Kala-azar + Malaria","Malarial Parasite (P.v + P.f)","Widal Test","Triple / Febrile Antigens","H.pylori Antibody"],
    "Coagulation": ["PT / INR / Prothrombin Time","APTT","D-Dimer","BT / CT"],
    "Microbiology": ["Gram Stain / V/S Study","Z-N Stain / AFB","Tuberculin / MT Test","Skin Scraping"],
    "Urinalysis": ["Urine RME / Routine & Microscopic","Urine Pregnancy Test","Ketone Bodies"],
    "Stool Analysis": ["Stool Examination","OBT / Occult Blood Test"],
    "Cardiac Markers": ["Troponin-I","NT-proBNP"],
    "Tumor Markers": ["PSA","AFP","CEA","CA-125"],
    "Pathology": ["FNAC / Cytopathology"],
  };
  const [allData, setAllData]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [msg, setMsg]                   = useState("");
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [editingName, setEditingName]   = useState(null);
  const [newName, setNewName]           = useState("");
  const [assigningReport, setAssigningReport] = useState(null);
  const [assigningCategory, setAssigningCategory] = useState(null);
  const [assigningSubtype, setAssigningSubtype]   = useState(null);
  const [showOther, setShowOther]       = useState(false);
  const [clearingAll, setClearingAll]   = useState(false);
  const [confirmDlg, setConfirmDlg]     = useState(null); // {title,message,confirmLabel,variant,onConfirm}

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };
  const showConfirm = (opts) => new Promise(resolve => setConfirmDlg({ ...opts, onConfirm: () => { setConfirmDlg(null); resolve(true); }, onCancel: () => { setConfirmDlg(null); resolve(false); } }));

  const load = () => {
    setLoading(true);
    reportAPI.getAllCategories().then(d => { setAllData(d); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div style={{ padding: 20, color: "#64748B" }}>Loading...</div>;

  const approved     = (allData?.custom || []).filter(c => c.status === "approved");
  const otherReports = allData?.other_reports || [];

  const handleCreate = async () => {
    const name = newCategoryInput.trim();
    if (!name) return;
    setSaving(true);
    try {
      await reportAPI.addCategory(name, "approved");
      load(); setNewCategoryInput(""); flash(`Added "${name}"`);
    } catch { flash("Failed"); } finally { setSaving(false); }
  };

  const handleViewReport = (r) => {
    if (!r.patient_id || !r.id) return;
    // Set both keys so Patients.js can read them on mount (timing safety)
    sessionStorage.setItem("emr_selected_patient", r.patient_id);
    sessionStorage.setItem("emr_nav_rep", r.id);
    window.dispatchEvent(new CustomEvent("emr:navigate", { detail: { page: "patients", patientId: r.patient_id, reportId: r.id } }));
  };

  const handleDelete = async (name) => {
    const ok1 = await showConfirm({ title: `Delete "${name}"?`, message: "All reports under this category will move to Other. This can't be undone.", confirmLabel: "Delete", variant: "danger" });
    if (!ok1) return;
    setSaving(true);
    try {
      await reportAPI.deleteCategory(name);
      load(); flash(`Deleted "${name}"`);
    } catch { flash("Failed"); } finally { setSaving(false); }
  };

  const handleRenameAny = async (oldName, newName) => {
    if (!newName.trim() || newName.trim() === oldName) return;
    setSaving(true);
    try {
      await reportAPI.renameCategory(oldName, newName.trim());
      load(); flash(`Renamed "${oldName}" to "${newName.trim()}"`);
    } catch { flash("Failed to rename"); } finally { setSaving(false); }
  };

  const handleClearAll = async () => {
    const ok2 = await showConfirm({ title: "Clear all other entries?", message: "Reports will stay under Other category — only the list entries are removed.", confirmLabel: "Clear all", variant: "warning" });
    if (!ok2) return;
    setClearingAll(true);
    flash("Clearing...");
    try {
      await reportAPI.clearAllOther();
      load(); setShowOther(false);
      flash("Cleared all Other entries");
    } catch { flash("Failed to clear"); } finally { setClearingAll(false); }
  };

  const handleClearOne = async (typeName) => {
    setSaving(true);
    try {
      await reportAPI.clearOtherEntry(typeName);
      load(); flash(`Cleared "${typeName}"`);
    } catch { flash("Failed"); } finally { setSaving(false); }
  };

  const handleAssign = async (reportId, category) => {
    setSaving(true);
    try {
      await reportAPI.assignOtherReport(reportId, category);
      load(); setAssigningReport(null); flash(`Report assigned to "${category}" — statuses recalculated`);
    } catch { flash("Failed"); } finally { setSaving(false); }
  };

  return (
    <div>
      {msg && <div style={{ marginBottom: 12, padding: "8px 12px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, fontSize: 13, color: "#166534" }}>{msg}</div>}

      {/* Predefined + Approved Categories */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div style={{ fontWeight: 700 }}>Predefined categories</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>Admin can rename or delete — deleted reports move to Other</div>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {PREDEFINED.filter(c => c !== "Other").map(c => (
              <CategoryPill key={c} name={c} onDelete={() => handleDelete(c)}
                onRename={(oldN, newN) => handleRenameAny(oldN, newN)} saving={saving} />
            ))}
            {/* Only admin-explicitly-added categories (not staff-typed Other names) */}
            {approved.filter(c => c.admin_created).map(c => (
              <CategoryPill key={c.name} name={c.name} isCustom
                onDelete={() => handleDelete(c.name)}
                onRename={(oldN, newN) => handleRenameAny(oldN, newN)} saving={saving} />
            ))}
            {/* Other pill — clickable to show Other reports */}
            <div onClick={() => setShowOther(s => !s)}
              style={{ display: "flex", alignItems: "center", gap: 4,
                background: showOther ? "#FEE2E2" : "#F1F5F9", borderRadius: 99,
                padding: "3px 12px", cursor: "pointer",
                border: showOther ? "1px solid #FCA5A5" : "1px solid #E2E8F0" }}>
              <span style={{ color: showOther ? "#DC2626" : "#64748B", fontSize: 13, fontWeight: 500 }}>Other</span>
              {otherReports.length > 0 && (
                <span style={{ fontSize: 10, fontWeight: 700, background: "#DC2626", color: "#fff", borderRadius: 99, padding: "1px 5px" }}>
                  {otherReports.length}
                </span>
              )}
              <span style={{ color: "#94A3B8", fontSize: 11 }}>{showOther ? "▲" : "▼"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Category */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div style={{ fontWeight: 700 }}>Add new category</div>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", gap: 8 }}>
            <input className="input" value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
              placeholder="New category name..." style={{ flex: 1, padding: "6px 10px", fontSize: 13 }} />
            <button className="btn btn-primary btn-sm" onClick={handleCreate} disabled={saving || !newCategoryInput.trim()}>+ Add</button>
          </div>
        </div>
      </div>

      {/* Other reports — expand when Other pill clicked */}
      {showOther && (
        <div className="card" style={{ marginBottom: 16, border: "1px solid #FED7AA" }}>
          <div className="card-header" style={{ background: "#FFF7ED", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 700, color: "#92400E" }}>Other category reports ({otherReports.length})</div>
              <div style={{ fontSize: 12, color: "#B45309" }}>Reports uploaded under Other — assign to a proper category or clear</div>
            </div>
            {otherReports.length > 0 && canAction && (
              <button className="btn btn-ghost btn-sm" onClick={handleClearAll} disabled={clearingAll}
                style={{ fontSize: 12, color: "#DC2626", border: "1px solid #FECACA" }}>
                🗑 Clear all
              </button>
            )}
          </div>
          {otherReports.length === 0 ? (
            <div className="card-body" style={{ color: "#94A3B8", fontSize: 13 }}>No Other reports.</div>
          ) : (
            <div className="card-body">
              {otherReports.map(r => (
                <div key={r.id} style={{ padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#92400E" }}>{r.type}</span>
                      {r.patient_name && (
                        <span style={{ fontSize: 11, color: "#64748B", marginLeft: 8 }}>
                          {r.patient_name} · {r.date}
                        </span>
                      )}
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleViewReport(r)} style={{ fontSize: 11 }}>
                      👁 View report
                    </button>
                    {assigningReport === r.id ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                        {/* Step 1: pick category */}
                        <select value={assigningCategory || ""} onChange={e => { setAssigningCategory(e.target.value || null); setAssigningSubtype(null); }}
                          style={{ fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8" }}>
                          <option value="" disabled>Category...</option>
                          {PREDEFINED.filter(p => p !== "Other").map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                        {/* Step 2: pick sub-type */}
                        {assigningCategory && (
                          <select value={assigningSubtype || ""} onChange={e => setAssigningSubtype(e.target.value || null)}
                            style={{ fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8" }}>
                            <option value="" disabled>Sub-type...</option>
                            <option value={assigningCategory}>{assigningCategory} (general)</option>
                            {(REPORT_SUBTYPES[assigningCategory] || []).map(sub => (
                              <option key={sub} value={sub}>{sub}</option>
                            ))}
                          </select>
                        )}
                        {/* Step 3: confirm */}
                        {assigningSubtype && (
                          <button onClick={() => { handleAssign(r.id, assigningSubtype); setAssigningCategory(null); setAssigningSubtype(null); }}
                            style={{ fontSize: 11, padding: "3px 8px", borderRadius: 5, border: "1px solid #BBF7D0", background: "#F0FDF4", color: "#15803D", cursor: "pointer", fontWeight: 600 }}>
                            ✓ Save
                          </button>
                        )}
                        <button onClick={() => { setAssigningReport(null); setAssigningCategory(null); setAssigningSubtype(null); }}
                          style={{ fontSize: 11, padding: "2px 6px", border: "0.5px solid #E2E8F0", borderRadius: 4, background: "transparent", cursor: "pointer", color: "#64748B" }}>✕</button>
                      </div>
                    ) : (
                      <button className="btn btn-ghost btn-sm" onClick={() => { setAssigningReport(r.id); setAssigningCategory(null); setAssigningSubtype(null); }}
                        style={{ fontSize: 11, color: "#1D4ED8", border: "1px solid #BFDBFE", background: "#EFF6FF" }}>
                        Assign
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => handleClearOne(r.type)} disabled={saving}
                      style={{ fontSize: 11, color: "#DC2626", border: "1px solid #FECACA" }}>
                      Clear
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Imaging Modalities */}
      <ImagingModalitiesSection showConfirm={showConfirm} />
      {confirmDlg && <ConfirmDialog open={true} title={confirmDlg.title} message={confirmDlg.message}
        confirmLabel={confirmDlg.confirmLabel} variant={confirmDlg.variant}
        onConfirm={confirmDlg.onConfirm} onCancel={confirmDlg.onCancel} />}
    </div>
  );
}

// ── Imaging Modalities Section ────────────────────────────────────────────────
const MODALITY_COLORS = {
  "X-Ray":        { bg:"#EEF2FF", color:"#4F46E5", border:"#C7D2FE" },
  "CT":           { bg:"#CCFBF1", color:"#0D9488", border:"#99F6E4" },
  "MRI":          { bg:"#F5F3FF", color:"#7C3AED", border:"#DDD6FE" },
  "Ultrasound":   { bg:"#FEF3C7", color:"#D97706", border:"#FDE68A" },
  "Mammography":  { bg:"#FDF2F8", color:"#DB2777", border:"#FBCFE8" },
  "ECG":          { bg:"#DCFCE7", color:"#059669", border:"#BBF7D0" },
  "Echo":         { bg:"#E0F2FE", color:"#0284C7", border:"#BAE6FD" },
  "Color Doppler":{ bg:"#FFF7ED", color:"#EA580C", border:"#FED7AA" },
  "OPG":          { bg:"#F0FDF4", color:"#65A30D", border:"#D9F99D" },
  "DEXA/BMD":     { bg:"#FAF5FF", color:"#9333EA", border:"#E9D5FF" },
  "PET-CT":       { bg:"#FEF2F2", color:"#DC2626", border:"#FECACA" },
  "custom":       { bg:"#FEF9C3", color:"#92400E", border:"#FDE68A" },
};

function ModalityPill({ name, isCustom, onDelete, onRename, saving }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(name);
  const mc = MODALITY_COLORS[name] || MODALITY_COLORS["custom"];
  if (editing) return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:99, border:`0.5px solid ${mc.border}`, background:mc.bg }}>
      <input value={val} onChange={e => setVal(e.target.value)} autoFocus
        style={{ border:"none", background:"transparent", fontSize:13, fontWeight:500, color:mc.color, width:Math.max(val.length*8, 60), outline:"none" }}
        onKeyDown={e => { if(e.key==="Enter"){ onRename(name, val); setEditing(false); } if(e.key==="Escape") setEditing(false); }} />
      <span onClick={() => { onRename(name, val); setEditing(false); }} style={{ cursor:"pointer", fontSize:11, color:"#059669" }}>✓</span>
      <span onClick={() => setEditing(false)} style={{ cursor:"pointer", fontSize:11, color:"#94A3B8" }}>✕</span>
    </span>
  );
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:99, border:`0.5px solid ${mc.border}`, background:mc.bg, fontSize:13, fontWeight:500, color:mc.color }}>
      {name}
      <span onClick={() => { setEditing(true); setVal(name); }} style={{ fontSize:11, opacity:.5, cursor:"pointer" }} title="Rename">✎</span>
      <span onClick={() => !saving && onDelete(name)} style={{ fontSize:11, opacity:.5, cursor:"pointer", color:"#EF4444" }} title="Delete">✕</span>
    </span>
  );
}

function ImagingModalitiesSection({ showConfirm }) {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [msg,           setMsg]           = useState("");
  const [newMod,        setNewMod]        = useState("");
  const [showUncat,     setShowUncat]     = useState(false);
  const [reassigning,   setReassigning]   = useState({});
  const { user } = useApp();

  const [initialLoad,   setInitialLoad]   = useState(true);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };

  const load = (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    imagingModalityAPI.list()
      .then(d => { setData(d); setLoading(false); setInitialLoad(false); })
      .catch(() => { setLoading(false); setInitialLoad(false); });
  };
  useEffect(() => { load(true); }, []);

  const handleRename = async (old_name, new_name) => {
    if (!new_name.trim() || new_name.trim() === old_name) return;
    setSaving(true);
    try {
      await imagingModalityAPI.rename(old_name, new_name.trim());
      load(); flash(`Renamed "${old_name}" to "${new_name.trim()}"`);
    } catch { flash("Failed to rename"); } finally { setSaving(false); }
  };

  const handleDelete = async (name) => {
    const ok3 = await showConfirm({ title: `Delete "${name}"?`, message: "All studies with this modality will move to Uncategorized. This can't be undone.", confirmLabel: "Delete", variant: "danger" });
    if (!ok3) return;
    setSaving(true);
    try {
      await imagingModalityAPI.delete(name);
      load(); flash(`Deleted "${name}" — affected studies moved to Uncategorized`);
    } catch { flash("Failed to delete"); } finally { setSaving(false); }
  };

  const handleAdd = async () => {
    const name = newMod.trim();
    if (!name) return;
    setSaving(true);
    try {
      // Save to custom_modalities in system_settings via backup API
      await imagingModalityAPI.save(name);
      load(); setNewMod(""); flash(`Added "${name}"`);
    } catch { flash("Failed to add"); } finally { setSaving(false); }
  };

  const handleReassign = async (imagingId, modality) => {
    if (!modality) return;
    setSaving(true);
    try {
      await imagingModalityAPI.reassign(imagingId, modality);
      load(); flash(`Study reassigned to "${modality}"`);
    } catch { flash("Failed to reassign"); } finally { setSaving(false); }
  };

  const handleViewStudy = (study) => {
    sessionStorage.setItem("emr_img_level",        "viewer");
    sessionStorage.setItem("emr_img_patient_id",   study.patient_id);
    sessionStorage.setItem("emr_img_patient_name", study.patient_name);
    sessionStorage.setItem("emr_img_study_id",     study.id);
    window.dispatchEvent(new CustomEvent("emr:navigate", { detail: { page: "imaging" } }));
  };

  if (loading && initialLoad) return <div style={{ padding:20, color:"#64748B", fontSize:13 }}>Loading imaging modalities...</div>;

  const predefined   = data?.predefined   || [];
  const custom       = data?.custom       || [];
  const uncategorized = data?.uncategorized || [];
  const allModalities = [...predefined, ...custom];

  return (
    <div className="card" style={{ marginTop:16 }}>
      {msg && <div style={{ margin:"0 0 12px", padding:"8px 12px", background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:8, fontSize:13, color:"#166534" }}>{msg}</div>}
      <div className="card-header">
        <div style={{ fontWeight:700 }}>Imaging Modalities</div>
        <div style={{ fontSize:12, color:"#64748B" }}>Admin can rename or delete — deleted studies move to Uncategorized</div>
      </div>
      <div className="card-body">

        {/* Predefined */}
        <div style={{ fontSize:11, fontWeight:600, letterSpacing:".06em", textTransform:"uppercase", color:"#94A3B8", marginBottom:8 }}>Predefined</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          {predefined.map(m => (
            <ModalityPill key={m} name={m} onDelete={handleDelete} onRename={handleRename} saving={saving} />
          ))}
        </div>

        {/* Custom */}
        {custom.length > 0 && (
          <>
            <div style={{ height:.5, background:"#E2E8F0", margin:"4px 0 14px" }} />
            <div style={{ fontSize:11, fontWeight:600, letterSpacing:".06em", textTransform:"uppercase", color:"#94A3B8", marginBottom:8 }}>Custom (staff-added)</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16 }}>
              {custom.map(m => (
                <ModalityPill key={m} name={m} isCustom onDelete={handleDelete} onRename={handleRename} saving={saving} />
              ))}
            </div>
          </>
        )}

        {/* Uncategorized */}
        <div style={{ height:.5, background:"#E2E8F0", margin:"4px 0 14px" }} />
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: showUncat ? 12 : 0 }}>
          <div style={{ fontSize:11, fontWeight:600, letterSpacing:".06em", textTransform:"uppercase", color:"#94A3B8" }}>Uncategorized</div>
          <div onClick={() => setShowUncat(s => !s)} style={{
            display:"inline-flex", alignItems:"center", gap:6,
            padding:"4px 12px", borderRadius:99, cursor:"pointer",
            background: showUncat ? "#F1F5F9" : "#F8FAFC",
            border:".5px solid #CBD5E1", fontSize:13, fontWeight:500, color:"#475569",
          }}>
            Uncategorized
            {uncategorized.length > 0 && (
              <span style={{ background:"#475569", color:"#fff", borderRadius:99, padding:"1px 7px", fontSize:11, fontWeight:700 }}>
                {uncategorized.length}
              </span>
            )}
            <span style={{ fontSize:11, opacity:.6 }}>{showUncat ? "▲" : "▼"}</span>
          </div>
        </div>

        {showUncat && (
          <div style={{ background:"#F8FAFC", border:".5px solid #E2E8F0", borderRadius:10, padding:"12px 16px", marginBottom:16 }}>
            {uncategorized.length === 0 ? (
              <div style={{ fontSize:13, color:"#94A3B8", textAlign:"center", padding:"12px 0" }}>No uncategorized studies.</div>
            ) : uncategorized.map(s => (
              <div key={s.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom:".5px solid #E2E8F0" }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:500, color:"#0F172A" }}>{s.patient_name} · {s.patient_id}</div>
                  <div style={{ fontSize:11, color:"#94A3B8", marginTop:2 }}>{s.date} · {s.file_name}</div>
                  {s.original_modality && (
                    <span style={{ display:"inline-block", marginTop:4, padding:"2px 8px", borderRadius:4, fontSize:11, fontWeight:500, background:"#FEE2E2", color:"#991B1B" }}>
                      was: {s.original_modality}
                    </span>
                  )}
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleViewStudy(s)} style={{ fontSize:12 }}>
                    View →
                  </button>
                  <select
                    defaultValue=""
                    onChange={e => { setReassigning(r => ({ ...r, [s.id]: e.target.value })); }}
                    style={{ fontSize:12, padding:"5px 8px", borderRadius:6, border:".5px solid #E2E8F0", fontFamily:"inherit" }}>
                    <option value="" disabled>Select modality...</option>
                    {allModalities.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleReassign(s.id, reassigning[s.id])}
                    disabled={!reassigning[s.id] || saving}
                    style={{ fontSize:12 }}>
                    Assign
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add new */}
        <div style={{ height:.5, background:"#E2E8F0", margin:"4px 0 14px" }} />
        <div style={{ fontSize:11, fontWeight:600, letterSpacing:".06em", textTransform:"uppercase", color:"#94A3B8", marginBottom:8 }}>Add new modality</div>
        <div style={{ display:"flex", gap:8 }}>
          <input className="input" value={newMod} onChange={e => setNewMod(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            placeholder="New imaging modality name..." style={{ flex:1, padding:"6px 10px", fontSize:13 }} />
          <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving || !newMod.trim()}>+ Add</button>
        </div>
      </div>
    </div>
  );
}
